import {BehaviorSubject, distinctUntilChanged, Observable} from 'rxjs';
import {
  CustomAttributeInput,
  FilterContextInput,
  GetVisualDoorsFilterOptionsQuery,
} from '../../../generated/graphql';
import {CustomAttribute} from "../customer/custom-attribute.model";
import {CustomerService} from "../customer/customer.service";
import {Product} from "../../models/product";
import {mapSmallGraphQLProductTypeToSmallProductType, SmallProductType} from "../product-type/product-type";

export interface SortingOption {
  id: string
  name: string
}

export interface CustomAttributesFilter {
  attributeName: string
  values?: Array<string>
}

export interface CustomAttributeFilterOption {
  label: string;
  img: string;
  customAttribute: CustomAttributesFilter
}

export interface GenericFilter {
  categories?: Array<GenericFilterCategory>

  sortingOptions?: Array<SortingOption>
  selectedSortingOptionId?: string

  doorTypeOptions?: Array<DoorTypeFilterOption>
  selectedDoorType?: SmallProductType

  heroFilterOptions?: Array<HeroFilter>

  onlyFavorites?: boolean

  referralVisualDoorExternalId?: string

  productType?: string
  selectedProductType?: string

  customAttributes?: Array<CustomAttributesFilter> | null

  customAttributesFilterOptions?: Array<CustomAttributeFilterOption>

  customAttributesV2?: Array<CustomAttributeWithValues>
}

export type CustomAttributeWithValues = CustomAttribute & {
  selectedValues?: Array<string>
}

export interface HeroFilter {
  imageUrl: string
  title: string
  description?: string
  categoryOptionsToSelect?: Array<{
    categoryId: string
    selectAllOptions?: boolean
    optionsToSelect?: Array<string>
  }>
  enabled?: boolean
  selected?: boolean
}

export interface DoorTypeFilterOption {
  doorType: SmallProductType
  description: string
  svgImageUrl?: string
}

export type SelectedFilterCategoryOptions = {
  categoryId: string
  selectedOptionIds: Array<string>
};

export interface GenericFilterCategory {
  description?: string | null
  enabled: boolean
  id: string
  name: string
  options: Array<VisualDoorFilterCategoryOption>;
  show?: boolean
}

export interface VisualDoorFilterCategoryOption {
  count?: number
  description: string
  id: string
  imageUrl?: string
  selected?: boolean
}

export function transformGenericFilterIntoFilterInput(genericFilter: GenericFilter): FilterContextInput {
  return {
    sortBy: genericFilter.selectedSortingOptionId,
    doorTypeId: genericFilter.selectedDoorType?.id,
    onlyFavorites: genericFilter.onlyFavorites,
    filterFor: genericFilter.categories?.map(category => {
      return {
        categoryId: category.id,
        selectedOptionIds: category.options.filter(option => option.selected).map(option => option.id)
      }
    }).filter(cat => cat.selectedOptionIds.length !== 0),

    referralVisualDoorExternalId: genericFilter.referralVisualDoorExternalId,

    customAttributes: [
      ...(genericFilter.customAttributes || []).filter(attr => attr.values && attr.values.length > 0) as Array<CustomAttributeInput>,
      ...(genericFilter.customAttributesV2 || []).filter(attr => attr.selectedValues && attr.selectedValues.length > 0).map(attr => ({
        attributeName: attr.name,
        values: attr.selectedValues || []
      }))
    ]
  }
}

export function mapGraphqlFilterToGenericFilter({graphQLFilterOptions, previousFilter}: {
  graphQLFilterOptions?: GetVisualDoorsFilterOptionsQuery['getVisualDoorsFilterOptions'],
  previousFilter: GenericFilter
}): GenericFilter | undefined {
  if (!graphQLFilterOptions) return undefined

  return {
    ...previousFilter,
    heroFilterOptions: graphQLFilterOptions?.heroFilterOptions?.map((heroFilterOption => {

      const existingHeroFilter = previousFilter.heroFilterOptions?.find(hf => hf.title === heroFilterOption.title)

      return {
        ...heroFilterOption,
        description: heroFilterOption.description || undefined,
        title: heroFilterOption.title,
        selected: existingHeroFilter?.selected,
        enabled: existingHeroFilter?.enabled,
        categoryOptionsToSelect: heroFilterOption.categoryOptionsToSelect?.map(optionToSelect => {
          return {
            ...optionToSelect,
            selectAllOptions: optionToSelect.selectAllOptions || false,
            optionsToSelect: optionToSelect.optionsToSelect || []
          }
        })
      }
    })),
    doorTypeOptions: graphQLFilterOptions?.doorTypeOptions?.map(doorTypeOption => {
      return {
        ...doorTypeOption,
        doorType: mapSmallGraphQLProductTypeToSmallProductType(doorTypeOption.doorType),
        svgImageUrl: doorTypeOption.svgImageUrl || undefined,
      }
    }),
    sortingOptions: graphQLFilterOptions?.sortingOptions || undefined,
    categories: graphQLFilterOptions?.categories ? graphQLFilterOptions.categories.map(category => {

      const previousCategorySelection = previousFilter?.categories?.find(
        (cat) => cat.id === category.id,
      )

      return {
        ...category,
        show: previousFilter?.categories?.find(cat => category.id === cat.id)?.show,
        options: category.options.map(option => {
          return {
            ...option,
            count: option.count || undefined,
            imageUrl: option.imageUrl || undefined,
            selected: previousCategorySelection?.options.find(opt => opt.id === option.id)?.selected
          }
        })
      }
    }) : undefined
  }
}

export abstract class FilterService<T extends GenericFilter = GenericFilter> {
  onlyShowFavorites = new BehaviorSubject(false);

  constructor(protected readonly customerService: CustomerService) {
  }

  abstract loadFilterOptions(prevFilter: T): void;

  abstract getFilter(): T | undefined

  abstract checkForWrongDoorTypes(): boolean

  abstract getFilter$(): Observable<T | undefined>

  abstract setFilter(newFilter: T | undefined): void;

  abstract isFilterFeatureEnabled$(location: 'topBar' | 'sideMenue'): Observable<boolean>;

  abstract getFilterOptions$(): Observable<T | undefined>

  abstract getFilterOptionsValue(): T | undefined

  abstract setReferralDoor(door: Product | undefined): void;

  getOnlyShowFavorites(): boolean {
    return this.onlyShowFavorites.getValue();
  };

  getOnlyShowFavorites$(): Observable<boolean> {
    return this.onlyShowFavorites.pipe(distinctUntilChanged());
  };

  setOnlyShowFavorites(onlyShowFavorites: boolean): void {
    this.onlyShowFavorites.next(onlyShowFavorites);
  }

  abstract loadSortingOptions(): void;

  abstract getSortingOptions$(): Observable<Array<SortingOption>>;

  abstract getSelectedSorting$(): Observable<string | undefined>;
}
